import React from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { CommandPalette } from './CommandPalette';
import type { RecentPage } from '../../App';
import type { RequestApprovalTypeSummaryItem } from '../../types/EntityEntitlements';

type NavigationCategory = 'person' | 'locality' | 'other' | 'meta' | 'utilities' | 'blacklist' | 'admin';

interface AppShellProps {
    isDashboard: boolean;
    navRailOpen: boolean;
    onToggleNavRail: () => void;
    pageLabel: string;
    categoryLabel: string | null;
    requestApprovalSummary: RequestApprovalTypeSummaryItem[];
    onNavigate: (destination: string, isEntity?: boolean, category?: NavigationCategory) => void;
    onNavigateHash: (hash: string) => void;
    onImpersonationChange: () => void;
    selectedDestination?: string;
    activePageKey?: string;
    authResolved: boolean;
    recentPages: RecentPage[];
    onDismissRecentPage: (destination: string) => void;
    dashboardContent: React.ReactNode;
    routeContent: React.ReactNode;
    footerContent: React.ReactNode;
    dialogsContent: React.ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({
    isDashboard,
    navRailOpen,
    onToggleNavRail,
    pageLabel,
    categoryLabel,
    requestApprovalSummary,
    onNavigate,
    onNavigateHash,
    onImpersonationChange,
    selectedDestination,
    activePageKey,
    authResolved,
    recentPages,
    onDismissRecentPage,
    dashboardContent,
    routeContent,
    footerContent,
    dialogsContent,
}) => {
    const [commandPaletteOpen, setCommandPaletteOpen] = React.useState(false);

    return (
        <>
            <Header
                isDashboard={isDashboard}
                navRailOpen={navRailOpen}
                onToggleNavRail={onToggleNavRail}
                pageLabel={pageLabel}
                categoryLabel={categoryLabel}
                requestApprovalSummary={requestApprovalSummary}
                onNavigate={onNavigate}
                onImpersonationChange={onImpersonationChange}
                onOpenCommandPalette={() => setCommandPaletteOpen(true)}
            />

            <div className="flex flex-col" style={{ minHeight: 'calc(100vh - 56px)' }}>
                <main className="flex-1">
                    {/* Dashboard (always rendered, toggled by display) */}
                    <div style={{ display: isDashboard ? 'block' : 'none' }}>
                        {dashboardContent}
                    </div>

                    {/* Non-dashboard content */}
                    {!isDashboard && (
                        <div className="flex">
                            {navRailOpen && (
                                <Sidebar
                                    onNavigate={onNavigate}
                                    onNavigateHash={onNavigateHash}
                                    selectedDestination={selectedDestination}
                                    activePageKey={activePageKey}
                                    authResolved={authResolved}
                                    recentPages={recentPages}
                                    onDismissRecentPage={onDismissRecentPage}
                                />
                            )}
                            <div className="flex-1 min-w-0 px-6">
                                {routeContent}
                            </div>
                        </div>
                    )}
                </main>

                {dialogsContent}
                {footerContent}
            </div>

            <CommandPalette
                open={commandPaletteOpen}
                onOpenChange={setCommandPaletteOpen}
                onNavigate={onNavigate}
                onNavigateHash={onNavigateHash}
                authResolved={authResolved}
            />
        </>
    );
};
